/**
 * What Overview says when it has no figure, and what it may add up.
 *
 * Split out of `Overview.tsx` because fast refresh only works when a file
 * exports components alone — and because these are the decisions worth testing
 * without rendering anything (#140).
 */
import { formatMoney, sum } from '@/lib/money'
import type { Money } from '@/lib/money'
import type { AccountsState } from '@/accounts/accounts'
import type { Balance, BalanceState } from '@/accounts/balances'
import type { TransactionsState } from '@/activity/transactions'

/**
 * What the headline tile can honestly lead with.
 *
 * # It leads with `available`, not with `balance` (#170)
 *
 * It led with `balance` — our own book's position — and on a live account that
 * read **"Everything you hold — 100,000.00 EUR"** while the row directly beneath
 * it read `100,000.00 · 93,602.86 EUR available`. The screen contradicted
 * itself, and the number set in the display face was the larger, less
 * trustworthy one.
 *
 * `available` is `min(balance − pending_out, the institution's own figure)`, and
 * it is the figure that actually governs a cash-out. Leading with it cannot
 * overstate. Leading with `balance` can, and did.
 *
 * # The gap has two causes and they are not the same claim
 *
 * `capped_by` says which operand bound the result. It has been decoded,
 * validated and typed since `balances.ts` was written, and until this change it
 * was read nowhere — the one field published to answer this exact question.
 *
 *   `"ledger"`    the gap is `pending_out`. That money **is** the customer's;
 *                 it is spoken for. Naming it "reserved" is the whole truth.
 *   `"provider"`  the institution reports **less than our book says**. Our
 *                 figure is contradicted by the party that actually holds the
 *                 money, and there is no reservation to point at.
 *
 * The second is the stronger claim, so on a mixed set it wins: a total short by
 * an amount nobody can account for must not be described as merely reserved.
 *
 * # Why this is not a sum of `balance`
 *
 * Adding 8,240.00 EUR to 0.0412 BTC needs a valuation rate, and **nothing
 * publishes one to this app**. `GET /v1/fees/quote` prices a specific
 * conversion; it is not a market rate for stating what a portfolio is worth,
 * and using it as one would put a number on the screen that the backend never
 * said and cannot be reconciled against anything.
 *
 * Inventing it would also be arithmetic on amounts outside `money.ts`
 * (`CLAUDE.md` §3) and a cross-currency addition, which invariant 3 forbids
 * outright.
 *
 * So: a total exists only when every account shares one currency. Otherwise
 * the tile says what it holds and says why it cannot add them up. That reads as
 * a smaller claim than the fixture's confident `13,314.75 EUR`, and it is the
 * true one.
 *
 * # Nothing here is recomputed
 *
 * `available` is never re-derived from `balance − pending_out`. The backend
 * computes it with the same code the money path uses precisely so a display
 * figure cannot drift from the one that governs a withdrawal (invariant 2c),
 * and a second implementation on a screen is how that drift starts.
 *
 * # A partial read never becomes a total
 *
 * If any balance is still loading or unavailable, there is no total — not a
 * total of the ones that arrived. A figure that silently omits an account is
 * indistinguishable from one that does not, and the omitted account might be
 * the one holding the money.
 */
export type Shortfall =
  /** Nothing is committed, so the figure is the whole of it. */
  | { readonly kind: 'none' }
  /** The gap is an instruction already given. Still the customer's money. */
  | { readonly kind: 'reserved'; readonly reserved: Money }

type Held =
  | { readonly kind: 'loading' }
  | { readonly kind: 'total'; readonly total: Money; readonly shortfall: Shortfall }
  | { readonly kind: 'mixed'; readonly currencies: readonly string[] }
  | { readonly kind: 'partial' }
  | { readonly kind: 'none' }
  /**
   * We could not find out, which is NOT holding nothing (#22, #140).
   *
   * Caught in a browser: with `GET /v1/accounts` failing, this tile said
   * "Nothing yet — when you hold something, it will be here." to somebody who
   * may hold plenty. Telling a customer their wallet is empty when the truth is
   * that we could not read it is the mistake `accounts.ts` keeps `none` and
   * `unavailable` apart to prevent, and collapsing them here handed it back one
   * layer up.
   */
  | { readonly kind: 'unknown' }

export function totalHeld(
  accounts: AccountsState,
  balances: Readonly<Record<string, BalanceState>>,
): Held {
  if (accounts.status === 'loading') return { kind: 'loading' }
  if (accounts.status === 'unavailable') return { kind: 'unknown' }
  // `unregistered` and `none` both mean the customer genuinely holds nothing:
  // no wallet yet, or a wallet with no account. Neither is a failure to look.
  if (accounts.status !== 'ready') return { kind: 'none' }
  if (accounts.accounts.length === 0) return { kind: 'none' }

  const ready: Balance[] = []
  for (const a of accounts.accounts) {
    const b = balances[a.accountRef]
    if (b === undefined || b.status === 'loading') return { kind: 'loading' }
    if (b.status !== 'ready') return { kind: 'partial' }
    ready.push(b.balance)
  }

  // One currency per account is already guaranteed — `decodeBalance` refuses a
  // balance whose three figures disagree — so any one of the three answers this.
  const currencies = [...new Set(ready.map((b) => b.available.currency))]
  if (currencies.length > 1) return { kind: 'mixed', currencies }

  // `sum` refuses a mixed list itself; the check above is so this branch can
  // say something useful rather than throw.
  return { kind: 'total', total: sum(ready.map((b) => b.available)), shortfall: shortfallOf(ready) }
}

/**
 * Why the headline is short of what could be spent, if it is.
 *
 * # OUR OWN FIGURE IS NOT PUBLISHED HERE, and that changed (#174)
 *
 * This used to return a `provider` case carrying `sum(balance)` — our book's
 * total — so the tile could say "Our own records show 100,000.00 EUR; where the
 * two disagree, the smaller figure is the one you can use." Reported from the
 * screen, and the question it produced is the right one:
 *
 *   "100,000 but 93,000 available, in what period is this? is it 100,000
 *    forever? what if i get another 100,000 and spend them all what then?"
 *
 * It would have been. `capped_by: "provider"` is a reconciliation break, not a
 * hold: money left the institution and our books never recorded it, nothing
 * resolves it, and every future credit widens the pair rather than closing it.
 * Publishing our side of it invites a customer to reason about a number that
 * describes no account — and there is nothing they could do with the answer.
 *
 * So the only gap this reports is `pending_out`: money COMMITTED to a payment
 * already asked for, which is the gap a bank statement shows and the one that
 * settles. The break is an operations problem, and one already has an owner —
 * the stranded-movement sweep pages somebody about it (go-wallet#853, #1062).
 */
function shortfallOf(ready: readonly Balance[]): Shortfall {
  // Compared against zero rather than subtracted: a comparison is not
  // arithmetic on an amount, and there is no subtraction in `money.ts` to reach
  // for anyway.
  const reserved = sum(ready.map((b) => b.pendingOut))
  if (reserved.amountMinor === 0n) return { kind: 'none' }
  return { kind: 'reserved', reserved }
}

/**
 * The sentence under the headline figure.
 *
 * Here rather than in the component for this file's whole reason: it is a
 * decision worth testing without rendering anything.
 */
export function shortfallNote(shortfall: Shortfall): string {
  switch (shortfall.kind) {
    case 'none':
      return 'Across every account you hold.'
    case 'reserved':
      return (
        `Across every account you hold. ${formatMoney(shortfall.reserved, { withCurrency: true })} ` +
        'is already committed to a payment you asked for, so it is not counted here.'
      )
  }
}

/** What to say when there is no holdings table to draw. */
export function holdingsMessage(accounts: AccountsState): string {
  switch (accounts.status) {
    case 'loading':
      return 'Reading your accounts…'
    case 'none':
      // The contract is explicit that an empty list is a normal answer and a
      // 500 is not, and `accounts.ts` keeps them apart for the reason #22 made
      // expensive. Saying "you have no account" when we simply could not look
      // sends somebody to support about their onboarding.
      return 'You do not hold an account yet.'
    case 'unregistered':
      return 'Your wallet is not open yet, so there is nothing to hold.'
    case 'unavailable':
      return `We could not read your accounts. ${accounts.reason}`
    case 'ready':
      // Unreachable: the caller draws the table in this state. Returning ''
      // rather than throwing, because a copy helper is not where a render
      // should die.
      return ''
  }
}

/** What to say when there is no activity table to draw. */
export function activityMessage(activity: TransactionsState): string {
  switch (activity.status) {
    case 'loading':
      return 'Reading your history…'
    case 'none':
      return 'Nothing has moved yet.'
    case 'unavailable':
      return `We could not read your history. ${activity.reason}`
    case 'ready':
      return ''
  }
}
